import { z } from 'zod';

import { addDay } from './date';
import { validation } from './validation';

import type { AppEnv } from './appEnv';
import type {
  FetchLoggedInUserResponse,
  LoginSessionToken,
} from './loggedInUser';

// ログイン中のユーザーが持っているCookieのデータ型
export type LoggedInUserCookie = {
  user_id: string;
  login_session_token: LoginSessionToken;
  is_student: '0' | '1';
};

const loggedInUserCookieSchema = z.object({
  // eslint-disable-next-line no-magic-numbers
  user_id: z.string().min(1),
  // eslint-disable-next-line no-magic-numbers
  login_session_token: z.string().min(1),
  is_student: z.union([z.literal('0'), z.literal('1')]),
});

export const isLoggedInUserCookie = (
  value: unknown,
): value is LoggedInUserCookie =>
  validation(loggedInUserCookieSchema, value).isValidate;

const loggedInUserCookieNames = [
  'login_session_token',
  'user_id',
  'is_student',
] as const;

export type LoggedInUserCookieName = (typeof loggedInUserCookieNames)[number];

const loggedInUserCookieDomains = ['nurse-senka.jp', 'smsc-nsc.com'] as const;

export type LoggedInUserCookieDomain =
  (typeof loggedInUserCookieDomains)[number];

export type LoggedInUserSetCookieParam = {
  name: LoggedInUserCookieName;
  value: string;
  options: {
    domain: LoggedInUserCookieDomain;
    secure: boolean;
    httpOnly: boolean;
    expires: Date;
    path: string;
  };
};

export type LoggedInUserSetCookieOptions = {
  domain: LoggedInUserCookieDomain;
  secure: boolean;
  httpOnly: boolean;
  expires: Date;
  path: string;
};

export const getLoggedInUserSetCookieOptions = (params: {
  appEnv: AppEnv;
  nowDate: Date;
}): LoggedInUserSetCookieOptions => {
  // Cookieが先に消えてしまう事を防ぐため、トークンの有効期限より少し長めの値を設定しておく
  const loginSessionTokenCookieExpires = 15;

  const domain: LoggedInUserCookieDomain =
    params.appEnv === 'production' ? 'nurse-senka.jp' : 'smsc-nsc.com';

  const secure = params.appEnv === 'production';

  const httpOnly = false;

  const expires = addDay(params.nowDate, loginSessionTokenCookieExpires);

  const path = '/';

  return {
    domain,
    secure,
    httpOnly,
    expires,
    path,
  };
};

// eslint-disable-next-line max-lines-per-function
export const getLoggedInUserSetCookieParams = (params: {
  appEnv: AppEnv;
  fetchLoggedInUserResponse: FetchLoggedInUserResponse;
  nowDate: Date;
}): LoggedInUserSetCookieParam[] => {
  const options = getLoggedInUserSetCookieOptions({
    appEnv: params.appEnv,
    nowDate: params.nowDate,
  });

  return [
    {
      name: 'login_session_token',
      value: params.fetchLoggedInUserResponse.loginSessionToken,
      options,
    },
    {
      name: 'user_id',
      value: String(params.fetchLoggedInUserResponse.loggedInUser.sub),
      options,
    },
    {
      name: 'is_student',
      value: params.fetchLoggedInUserResponse.loggedInUser.isStudent
        ? '1'
        : '0',
      options,
    },
  ];
};

export const getLoggedInUserDeleteCookieNames = (): Readonly<
  LoggedInUserCookieName[]
> => loggedInUserCookieNames;
